import { useEffect, useRef, useState } from 'react';
import { FaBarcode } from 'react-icons/fa6';
import { IoClose } from 'react-icons/io5';
import { MdGrid3X3, MdInventory, MdSpeed } from 'react-icons/md';

import { useApp } from '@/context/app';
import { useSocketIO } from '@/context/socket';
import { useCartMode } from '@/hooks/useCartMode';
import { ReloadIcon } from '@radix-ui/react-icons';

import { EditPrice } from './EditPrice';
import { Image } from './Image';
import { QuantityModal } from './QuantityModal';

interface ProductProps {
	id?: number;
	barcode?: string;
	name: string;
	price?: number;
	coast?: number;
	stock?: number;
	unit?: string;
	OBS?: string;
}

const beep = new Audio('/bip-notfound.mp3');
function soundNotFound() {
	beep.play();
	if (navigator && navigator.vibrate) {
		navigator.vibrate([500, 100, 200, 100, 200]);
	}
}

export function Product({
	barcode,
	isLoading,
	loading,
	directProduct,
}: {
	barcode: string;
	isLoading: (value: boolean) => void;
	loading: boolean;
	directProduct?: ProductProps | null;
}) {
	const [product, setProduct] = useState<ProductProps | null>({
		name: 'PRONTO PARA INICIAR',
		OBS: 'Faça sua primeira consulta agora!',
	});
	const { socket, statusSocket } = useSocketIO();
	const [secondsExec, setSecondsExec] = useState(0);
	const { isTV, connection } = useApp();
	const mode = useCartMode();
	const pendingBarcodeRef = useRef<string | null>(null);

	// When a product is selected directly (from search), show it immediately
	useEffect(() => {
		if (directProduct) {
			setProduct(directProduct);
			isLoading(false);
		}
	}, [directProduct]);

	function updateProduct(data: any) {
		if (data.length === 0) {
			soundNotFound();
			setProduct({
				name: 'NÃO ENCONTRADO',
				price: 0,
				barcode: barcode,
				id: 0,
			});
			return;
		}

		setProduct(data[0]);
	}

	useEffect(() => {
		switch (statusSocket) {
			case 'reconnected':
				setProduct(() => ({
					name: 'RECONECTANDO AO SERVIDOR...',
					OBS: 'Estamos estabelecendo conexão novamente, aguarde...',
				}));
				break;
			case 'connecting':
				setProduct(() => ({
					name: 'CONECTANDO AO SERVIDOR...',
					OBS: 'Estamos conectando ao servidor, aguarde...',
				}));
				break;
			case 'disconnected':
				isLoading(false);
				setProduct(() => ({
					name: 'DESCONECTADO',
					OBS: mode.disconnectedOBS,
				}));
				break;
			case 'connected':
				setProduct(() => ({
					name: mode.readyTitle,
					OBS: mode.readyOBS,
				}));
				break;
			case 'business_not_found':
				setProduct(() => ({
					name: 'EMPRESA NÃO CADASTRADA',
					OBS: 'Opa! O CNPJ informado não está em nosso sistema. Atualize a página e tente novamente! Se o problema persistir entre em contato com o suporte.',
				}));
				break;
			case 'token_invalid':
				setProduct(() => ({
					name: 'TOKEN INVÁLIDO',
					OBS: 'Opa! O token informado é inválido. Atualize a página e tente novamente! Se o problema persistir entre em contato com o suporte.',
				}));
				break;
			case 'agent_not_found':
				setProduct(() => ({
					name: 'O AGENTE NÃO ESTÁ CONECTADO',
					OBS: 'Verifique se o agente está rodando na máquina servidor. Se o problema persistir entre em contato com o suporte.',
				}));
				break;
			default:
				break;
		}
	}, [statusSocket]);

	useEffect(() => {
		if (directProduct) return;

		if (!!barcode && statusSocket === 'connected') {
			isLoading(true);
			setProduct(() => ({
				name: 'BUSCANDO...',
				OBS: 'Estamos buscando as informações, um instante...',
			}));
			const start = performance.now();
			pendingBarcodeRef.current = null;

			// Cancela listener anterior se um novo barcode chegar antes da resposta
			socket.off('resultConsult');

			socket.emit('consult', {
				consult: barcode.length < 7 ? 'productByCode' : 'productByBarCode',
				data: barcode,
			});

			// Timeout de segurança: se não houver resposta em 10s,
			// detecta conexão stale e força reconexão automática
			const timeoutId = setTimeout(() => {
				socket.off('resultConsult');
				pendingBarcodeRef.current = barcode;
				setProduct({
					name: 'RECONECTANDO...',
					OBS: 'Conexão perdida detectada. Reconectando automaticamente...',
				});
				socket.disconnect();
				socket.connect();
			}, 10_000);

			socket.once('resultConsult', (response) => {
				clearTimeout(timeoutId);
				pendingBarcodeRef.current = null;
				updateProduct(response.data);
				isLoading(false);
				setSecondsExec(performance.now() - start);
			});

			return () => {
				clearTimeout(timeoutId);
				socket.off('resultConsult');
			};
		}

		if (!barcode && statusSocket === 'connected') {
			setProduct({
				name: mode.idleTitle,
				OBS: mode.idleOBS,
			});
		}

		// Scan feito enquanto desconectado: enfileira para retry ao reconectar
		if (!!barcode && statusSocket !== 'connected') {
			pendingBarcodeRef.current = barcode;
		}
	}, [barcode]);

	// Auto-retry: quando a conexão voltar, retenta a consulta que estava travada
	useEffect(() => {
		if (statusSocket !== 'connected' || !pendingBarcodeRef.current) return;

		const barcodeToRetry = pendingBarcodeRef.current;
		pendingBarcodeRef.current = null;
		isLoading(true);
		setProduct(() => ({
			name: 'BUSCANDO...',
			OBS: 'Estamos buscando as informações, um instante...',
		}));
		const start = performance.now();

		socket.emit('consult', {
			consult: barcodeToRetry.length < 7 ? 'productByCode' : 'productByBarCode',
			data: barcodeToRetry,
		});

		const timeoutId = setTimeout(() => {
			socket.off('resultConsult');
			isLoading(false);
			setProduct({
				name: 'ERRO DE CONEXÃO',
				OBS: 'Não foi possível realizar a consulta. Tente novamente.',
			});
		}, 10_000);

		socket.once('resultConsult', (response) => {
			clearTimeout(timeoutId);
			updateProduct(response.data);
			isLoading(false);
			setSecondsExec(performance.now() - start);
		});

		return () => {
			clearTimeout(timeoutId);
			socket.off('resultConsult');
		};
	}, [statusSocket]);

	const { isPurchaseOrder } = mode;

	const canEdit =
		Boolean(product?.id) &&
		product?.name !== 'NÃO ENCONTRADO' &&
		(connection.functions?.includes('edit_price') ||
			connection.functions?.includes('edit_stock'));

	const canCart =
		Boolean(product?.id) &&
		Boolean(product?.price) &&
		product?.name !== 'NÃO ENCONTRADO' &&
		(connection.functions?.includes('cart') || isPurchaseOrder);

	return (
		<div className={`${isTV ? 'max-w-[640px] scale-150' : ''} w-full`}>
			{product?.barcode && product?.name !== 'NÃO ENCONTRADO' ? (
				<Image
					src={`https://cdn-cosmos.bluesoft.com.br/products/${product?.barcode}`}
					alt=""
					className="mx-auto h-64 max-w-[90%] min-w-[10rem] rounded-2xl"
					typeLoading="bar"
				/>
			) : product?.name !== 'NÃO ENCONTRADO' ? (
				<div className="h-44" />
			) : (
				<div className="h-44">
					<IoClose className="h-36 w-full text-red-500" />
				</div>
			)}
			<div
				data-noresult={product?.name === 'NÃO ENCONTRADO'}
				className="text-primary mx-auto mt-5 flex w-[80%] max-w-[36rem] flex-col items-center justify-center rounded-2xl shadow-lg data-[noresult=true]:text-red-400"
			>
				<h1 className="w-full rounded-t-2xl bg-current p-3 py-2 text-center text-lg leading-6 font-semibold">
					<span className="flex items-center justify-center gap-1 text-white">
						{loading && <ReloadIcon className="h-4 w-4 animate-spin" />}
						{product?.name}
					</span>
				</h1>
				<div className="flex w-full flex-col items-center gap-4 rounded-b-2xl border bg-white p-4 pt-6">
					{product?.OBS && (
						<p className="text-foreground text-center font-medium">
							{product?.OBS}
						</p>
					)}

					<div className="flex flex-col items-center gap-1">
						{isPurchaseOrder && product?.coast != null && (
							<div className="flex flex-col items-center">
								<span className="text-xs font-semibold tracking-wide text-zinc-400 uppercase">
									Último Custo
								</span>
								<p className="text-primary text-4xl leading-10 font-bold">
									{product.coast.toLocaleString('pt-BR', {
										style: 'currency',
										currency: 'BRL',
									})}
									{product?.unit === 'KG' && (
										<span className="text-base text-zinc-500">/kg</span>
									)}
								</p>
							</div>
						)}
						<div className="flex items-center">
							<div className="flex flex-col items-center">
								{isPurchaseOrder && product?.coast != null && (
									<span className="text-xs font-semibold tracking-wide text-zinc-400 uppercase">
										Preço de Venda
									</span>
								)}
								<p className="text-foreground text-5xl leading-10 font-bold">
									{product?.price?.toLocaleString('pt-BR', {
										style: 'currency',
										currency: 'BRL',
									})}
									{product?.unit === 'KG' ? (
										<span className="text-base text-zinc-500">/kg</span>
									) : product?.unit && product.unit !== 'UN' ? (
										<span className="text-base text-zinc-500">
											/{product.unit.toLowerCase()}
										</span>
									) : (
										''
									)}
								</p>
							</div>
							{canEdit && (
								<EditPrice
									data={product}
									onSuccess={(updates) =>
										setProduct((prev) => ({ ...prev, ...updates }))
									}
								/>
							)}
						</div>
					</div>
					{product?.barcode && (
						<div className="flex flex-wrap justify-center gap-x-4">
							{isPurchaseOrder && product?.stock != null && (
								<p className="flex items-center gap-1">
									<MdInventory />
									<span className="text-foreground">
										Estoque: {product.stock} {product?.unit}
									</span>
								</p>
							)}
							{product?.id ? (
								<p className="flex items-center gap-1">
									<MdGrid3X3 className="" size={20} />
									<span className="text-foreground">{product?.id}</span>
								</p>
							) : (
								''
							)}
							<p className="flex items-center gap-2">
								<FaBarcode className="" />
								<span className="text-foreground">{product?.barcode}</span>
							</p>
							<p className="flex items-center gap-1">
								<MdSpeed size={20} className="" />
								<span className="text-foreground">
									{(secondsExec / 1000).toFixed(2)}s
								</span>
							</p>
						</div>
					)}
				</div>
			</div>

			{canCart && (
				<div className="mx-auto mt-5 flex w-[80%] max-w-[36rem] items-center justify-center gap-3">
					<QuantityModal product={product} />
				</div>
			)}
		</div>
	);
}
